import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback, useEffect } from 'react';
import { useMutableRef } from '@carnica/utils/hooks';
import { selectCaptchaAccessToken, selectQueuedMessages, useCaptchaContext } from "../../../../entities/captcha/model";
import { addQuickReplies } from "../../../../entities/content/model";
import { selectChatCofig, selectIsHistoryCalled, setErrorReason, setErrorScreenType, setIsHistoryCalled } from "../../../../entities/settings/model";
import { useSocketContext } from "../../../../entities/socket/model";
import { createNewMessages } from "../../../../shared/api";
import { ErrorReason, ErrorScreenType, SocketStatus, isDev } from "../../../../shared/config";
import { CHAT_SEND_MESSAGE_EVENT, getMessageText, useOnlineStatus } from "../../../../shared/lib";
import { FSMStages, LoadingStatus, getContentThunk, getHistoryThunk, useAppDispatch, useAppSelector, useFSM } from "../../../../shared/model";
import { setStatus } from "../../../../entities/message/model";
import { useStartOperatorMessage } from "./useStartOperatorMessage";
var getData = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(dispatch, zoneType) {
    var _history, content, history;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          dispatch(setStatus(LoadingStatus.loading));
          if (!(zoneType === 'authorized')) {
            _context.next = 9;
            break;
          }
          _context.next = 4;
          return dispatch(getHistoryThunk());
        case 4:
          _history = _context.sent;
          _context.next = 7;
          return dispatch(getContentThunk());
        case 7:
          content = _context.sent;
          return _context.abrupt("return", [_history, content]);
        case 9:
          _context.next = 11;
          return dispatch(getHistoryThunk());
        case 11:
          history = _context.sent;
          return _context.abrupt("return", [history, null]);
        case 13:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function getData(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();
export var useCommunicationInitialize = function useCommunicationInitialize(_ref2) {
  var sendMessage = _ref2.sendMessage,
    sendSocketMessage = _ref2.sendSocketMessage;
  var dispatch = useAppDispatch();
  var queuedMessages = useAppSelector(selectQueuedMessages);
  var isHistoryCalled = useAppSelector(selectIsHistoryCalled);
  var _useAppSelector = useAppSelector(selectChatCofig),
    authType = _useAppSelector.authType,
    userData = _useAppSelector.userData,
    zoneType = _useAppSelector.authZoneConfig.zoneType;
  var sendMessageRef = useMutableRef(sendMessage);
  var captchaToken = useAppSelector(selectCaptchaAccessToken);
  var _useSocketContext = useSocketContext(),
    reconnect = _useSocketContext.reconnect,
    socketStatus = _useSocketContext.socketStatus,
    setSocketStatus = _useSocketContext.setStatus;
  var _useCaptchaContext = useCaptchaContext(),
    captchaStage = _useCaptchaContext.captchaStage;
  var _useFSM = useFSM(),
    stage = _useFSM.stage,
    changeStage = _useFSM.changeStage;
  var withSettedHeaders = isDev || (zoneType === 'authorized' ? !!(userData !== null && userData !== void 0 && userData.token) && !!authType : !!captchaToken);
  var isSocketReady = socketStatus === SocketStatus.open;
  var isOnline = useOnlineStatus();
  var requestData = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      var _yield$getData, _yield$getData2, historyResultAction, contentResultAction;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            dispatch(setIsHistoryCalled(true));
            _context2.next = 3;
            return getData(dispatch, zoneType);
          case 3:
            _yield$getData = _context2.sent;
            _yield$getData2 = _slicedToArray(_yield$getData, 2);
            historyResultAction = _yield$getData2[0];
            contentResultAction = _yield$getData2[1];
            if (getHistoryThunk.fulfilled.match(historyResultAction) && (!contentResultAction || getContentThunk.fulfilled.match(contentResultAction))) {
              changeStage(FSMStages.CONNECTING);
            }
          case 8:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }));
    return function requestData() {
      return _ref3.apply(this, arguments);
    };
  }();
  var submitQueuedMessages = useCallback(function () {
    queuedMessages.forEach(function (message) {
      var _getMessageText;
      var _createNewMessages = createNewMessages((_getMessageText = getMessageText(message)) !== null && _getMessageText !== void 0 ? _getMessageText : '', {
          id: message.messageId,
          replyId: message.message.replyId
        }),
        historyMessage = _createNewMessages.historyMessage,
        socketMessage = _createNewMessages.socketMessage;
      sendSocketMessage(socketMessage, historyMessage);
    });
  }, [queuedMessages]);
  var onReconnectFailure = function onReconnectFailure() {
    changeStage(FSMStages.ERROR);
    dispatch(setErrorScreenType(ErrorScreenType.ERROR));
    dispatch(setErrorReason(ErrorReason.WEBSOCKET_CONNECTION));
  };
  useEffect(function () {
    var listener = function listener(e) {
      sendMessageRef.current(e.detail, undefined);
    };
    window.addEventListener(CHAT_SEND_MESSAGE_EVENT, listener);
    return function () {
      dispatch(setIsHistoryCalled(false));
      window.removeEventListener(CHAT_SEND_MESSAGE_EVENT, listener);
    };
  }, []);
  useEffect(function () {
    if (isOnline && withSettedHeaders && !isHistoryCalled && stage === FSMStages.LOAD_DATA) {
      void requestData();
    }
    if (stage === FSMStages.CAPTCHA_NEEDED && zoneType === 'unauthorized') {
      dispatch(addQuickReplies([]));
    }
  }, [withSettedHeaders, stage, isOnline]);
  useEffect(function () {
    if (stage === FSMStages.ACTIVE && isSocketReady && captchaStage === 'end') {
      submitQueuedMessages();
    }
    if (stage === FSMStages.RECONNECT && zoneType === 'authorized') {
      reconnect({
        onFailure: onReconnectFailure
      });
    }

    /**
     * Условия для реконнекта в НЗ после прохождения каптчи.
     * Устанавливаем статус сокета в idle для последующего переподключения сессии.
     * Требуется для условия подключения FSMStages.CONNECTING && SocketStatus.idle в хуке {@link useConnectionHandler}.
     */
    if (socketStatus === SocketStatus.closed && stage === FSMStages.CAPTCHA_NEEDED) {
      setSocketStatus(SocketStatus.idle);
    }
  }, [stage, zoneType, isSocketReady]);
  useStartOperatorMessage();
};